/**
 * useTutorSession Hook
 *
 * Manages the Addison Tutor voice session:
 * - Fetches signed URL + session token from voice-tutor-signed-url
 * - Starts ElevenLabs conversation with client tools
 * - Client tools update visual state with security validation
 * - Integrates speaker ID via sendContextualUpdate
 *
 * See spec: docs/superpowers/specs/2026-04-12-addison-tutor-design.md
 */

import { useState, useCallback, useRef } from 'react';
import { Conversation } from '@elevenlabs/client';
import { supabase } from '@/lib/supabase/client';
import { STANDARD_COLORS } from '@/lib/turn-color';
import { CELEBRATION_WHITELIST } from '@/components/tutor/constants';
import type { VisualState } from '@/components/tutor/TutorDisplay';

// =============================================================================
// Types
// =============================================================================

export type TutorSessionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface TutorSessionData {
  signedUrl: string;
  sessionToken: string;
  dynamicVariables: Record<string, string>;
  currentWords: string[];
  teachingSteps: Record<string, unknown[]>;
  householdMembers: Array<{ name: string; role: string }>;
}

interface UseTutorSessionOptions {
  childId: string;
  onSpeakerChange?: (name: string, role: string) => void;
  onMessage?: (msg: { role: string; text: string }) => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useTutorSession({ childId, onSpeakerChange, onMessage }: UseTutorSessionOptions) {
  const [status, setStatus] = useState<TutorSessionStatus>('idle');
  const [visualState, setVisualState] = useState<VisualState>({ type: 'idle' });
  const [transcript, setTranscript] = useState<Array<{ role: string; text: string }>>([]);
  const [sessionData, setSessionData] = useState<TutorSessionData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const conversationRef = useRef<Awaited<ReturnType<typeof Conversation.startSession>> | null>(null);
  const currentWordsRef = useRef<string[]>([]);
  const lastSpeechTimestampRef = useRef<number>(Date.now());
  const onSpeakerChangeRef = useRef(onSpeakerChange);
  const onMessageRef = useRef(onMessage);
  onSpeakerChangeRef.current = onSpeakerChange;
  onMessageRef.current = onMessage;

  // ---------------------------------------------------------------------------
  // Client tools with security validation (spec §9: H-4, L-7)
  // ---------------------------------------------------------------------------

  const buildClientTools = useCallback(() => ({
    show_consonant: async ({ letter }: { letter: string }) => {
      if (!/^[a-zA-Z]{1,4}$/.test(letter)) return 'Invalid letter';
      setVisualState({ type: 'consonant', letter });
      return 'Displayed';
    },
    show_color: async ({ color }: { color: string }) => {
      if (!STANDARD_COLORS.some(c => c.color_name === color)) return 'Invalid color';
      setVisualState({ type: 'color', color });
      return 'Displayed';
    },
    show_letter_on_color: async ({ letter, color }: { letter: string; color: string }) => {
      if (!/^[a-zA-Z]{1,4}$/.test(letter)) return 'Invalid letter';
      if (!STANDARD_COLORS.some(c => c.color_name === color)) return 'Invalid color';
      setVisualState({ type: 'letter_on_color', letter, color });
      return 'Displayed';
    },
    show_word: async ({ word, turn_code }: { word: string; turn_code: string }) => {
      if (currentWordsRef.current.length > 0 && !currentWordsRef.current.includes(word)) {
        return 'Invalid word';
      }
      setVisualState({ type: 'word', word, turnCode: turn_code });
      return 'Displayed';
    },
    show_celebration: async ({ message }: { message: string }) => {
      const safe = CELEBRATION_WHITELIST.includes(message) ? message : 'Great job!';
      setVisualState({ type: 'celebration', message: safe });
      return 'Displayed';
    },
    clear_display: async () => {
      setVisualState({ type: 'idle' });
      return 'Cleared';
    },
  }), []);

  // ---------------------------------------------------------------------------
  // Start Session
  // ---------------------------------------------------------------------------

  const startSession = useCallback(async () => {
    if (conversationRef.current || status === 'connecting') return;

    setStatus('connecting');
    setErrorMessage(null);

    try {
      // 1. Get auth token
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession?.access_token) {
        setStatus('error');
        setErrorMessage('Not authenticated');
        return;
      }

      // 2. Fetch signed URL + session token from edge function
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-tutor-signed-url`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authSession.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ childId }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.error) {
        setStatus('error');
        setErrorMessage(data.error || 'Failed to start session');
        return;
      }

      setSessionData(data);
      currentWordsRef.current = data.currentWords || [];

      // 3. Request mic permission (warm-up before SDK takes it)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(t => t.stop()); // release — SDK will acquire its own
      } catch {
        setStatus('error');
        setErrorMessage('Microphone access is required');
        return;
      }

      // 4. Start ElevenLabs conversation
      const conversation = await Conversation.startSession({
        signedUrl: data.signedUrl,
        dynamicVariables: data.dynamicVariables,
        clientTools: buildClientTools(),
        onConnect: () => {
          setStatus('connected');
          setErrorMessage(null);
          lastSpeechTimestampRef.current = Date.now();
        },
        onDisconnect: () => {
          setStatus('disconnected');
          setVisualState({ type: 'idle' });
          conversationRef.current = null;
        },
        onMessage: (message: { source: string; message: string }) => {
          lastSpeechTimestampRef.current = Date.now();
          const entry = {
            role: message.source === 'user' ? 'user' : 'agent',
            text: message.message,
          };
          setTranscript(prev => [...prev, entry]);
          onMessageRef.current?.(entry);
        },
        onError: (error: unknown) => {
          console.error('[TutorSession] Error:', error);
          setStatus('error');
          setErrorMessage('Connection error');
        },
        onModeChange: (mode: { mode: string }) => {
          if (mode.mode === 'listening') {
            lastSpeechTimestampRef.current = Date.now();
          }
        },
      });

      conversationRef.current = conversation;
    } catch (err) {
      console.error('[TutorSession] Start failed:', err);
      setStatus('error');
      setErrorMessage('Failed to start session');
    }
  }, [childId, status, buildClientTools]);

  // ---------------------------------------------------------------------------
  // End Session
  // ---------------------------------------------------------------------------

  const endSession = useCallback(async () => {
    try {
      await conversationRef.current?.endSession();
    } catch {
      // Ignore cleanup errors
    }
    conversationRef.current = null;
    setVisualState({ type: 'idle' });
    setStatus('disconnected');
  }, []);

  // ---------------------------------------------------------------------------
  // Speaker Update (called by sherpa-onnx or manual picker)
  // ---------------------------------------------------------------------------

  const updateSpeaker = useCallback((name: string, role: string) => {
    conversationRef.current?.sendContextualUpdate(
      `Speaker changed to: ${name} (${role})`
    );
    lastSpeechTimestampRef.current = Date.now();
    onSpeakerChangeRef.current?.(name, role);
  }, []);

  return {
    status,
    visualState,
    transcript,
    sessionData,
    errorMessage,
    lastSpeechTimestampRef,
    startSession,
    endSession,
    updateSpeaker,
  };
}
